import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, TransitionGroup as _TransitionGroup, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "image-generator-panel" }
const _hoisted_2 = { class: "image-generator-panel__header" }
const _hoisted_3 = { class: "image-generator-panel__header-title" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", null, "Text to image generator", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "image-generator-panel__divider image-generator-panel__divider--padded" }, null, -1)
const _hoisted_6 = { class: "image-generator-panel__unsupported-dialogue" }
const _hoisted_7 = { class: "image-generator-panel__unsupported-dialogue-text" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", { class: "image-generator-panel__unsupported-dialogue-link-text" }, "Explore Serato Studio", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", { class: "image-generator-panel__prompt" }, "Start by typing a detailed description of an image and the style you’d like it to be generated in", -1)
const _hoisted_10 = { class: "image-generator-panel__input" }
const _hoisted_11 = ["onKeydown"]
const _hoisted_12 = { class: "image-generator-panel__scrollable-container" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["src", "onDragstart"]
const _hoisted_15 = ["href"]
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", { class: "image-generator-panel__divider" }, null, -1)
const _hoisted_17 = { class: "image-generator-panel__divider" }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("div", { class: "image-generator-panel__info-block" }, [
  /*#__PURE__*/_createElementVNode("p", null, "You can experiment with styles like:"),
  /*#__PURE__*/_createElementVNode("ul", null, [
    /*#__PURE__*/_createElementVNode("li", null, "photograph, photorealism"),
    /*#__PURE__*/_createElementVNode("li", null, "comic, sketch, watercolor"),
    /*#__PURE__*/_createElementVNode("li", null, "digital art, 3D render"),
    /*#__PURE__*/_createElementVNode("li", null, "synthwave, vaporwave"),
    /*#__PURE__*/_createElementVNode("li", null, "impressionist, expressive, modernist art")
  ])
], -1)
const _hoisted_19 = { class: "image-generator-panel__experimental-feature" }

import AiIcon from '@/assets/AiIcon.svg'
import UnionIcon from '@/assets/Union.svg'
import FlaskIcon from '@/assets/FlaskIcon.svg'
import LockIcon from '@/assets/LockIcon.svg'
import ExternalLinkIcon from '@/assets/ExternalLinkIcon.svg'
import DallE from '@/assets/DallE.svg'
import DownloadIcon from '@/assets/DownloadIcon.svg'
import BigButton from '@/components/common/BigButton.vue'
import { GalleryItem } from '@/store/gallery/types'
import { ref, inject } from 'vue'
import useBackgroundGalleryItem from '@/composables/UseBackgroundGalleryItem'
import { Resolution } from '@/types'
import { Sws } from '@serato/sws-sdk'
import GtmHelper from '@/analytics/gtm-helper'
import { useBackgroundGalleryStore } from '@/store/gallery/background-store'
import { useEndUserProfileStore } from '@/store/user-profile'
import { useApplicationStore } from '@/store/application-store'


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageGeneratorPanel',
  emits: ["closePanel"],
  setup(__props, { emit: emits }: { emit: ({
  (event: 'closePanel'): void
}), expose: any, slots: any, attrs: any }) {

const defaultImageResolution = { height: 1024, width: 1024 }

const gtmHelper = GtmHelper.getInstance()
const endUserProfileStore = useEndUserProfileStore()
const backgroundStore = useBackgroundGalleryStore()
const applicationStore = useApplicationStore()
const sws: Sws | undefined = inject('$swsSdk')
const text = ref('')
const textAreaRef = ref<HTMLTextAreaElement | null>(null)
const isFetching = ref(false)
let requestStartTime = 0



const { selectBackground, onDragBackground, isSelectedBackground } = useBackgroundGalleryItem()

const studioPricingLink = process.env.VUE_APP_CLIENT_URI_SERATO_COM + '/studio/pricing'

const onGenerate = async () => {
  if (text.value !== '' && textAreaRef.value?.validity.valid && sws) {
    isFetching.value = true
    requestStartTime = Date.now()
    gtmHelper.triggerDallESearchEvent(text.value)
    const image = await sws.aiproxy.generateImage({ prompt: text.value })
      .catch(e => {
        let snackbarText = 'Error occurred'
        switch (e.code) {
        case 9001:
          snackbarText = 'This service is unavailable right now'
          gtmHelper.triggerDallERateLimitEvent()
          break
        case 9002:
          snackbarText = e.message
          gtmHelper.triggerDallERestrictedTextEvent(text.value)
          break
        }
        applicationStore.pushSnackbarMessage({
          header: 'Oops something went wrong',
          text: snackbarText,
          state: 'error'
        })
      }
      )

    if (image?.b64_image) {
      gtmHelper.triggerDallEReceiveImageEvent(Date.now() - requestStartTime)
      const galleryItem = base64ToGalleryItem(image.b64_image, defaultImageResolution)
      backgroundStore.generatedImages.unshift(galleryItem)
    }
    isFetching.value = false
  }
}

const base64ToGalleryItem = (base64Image: string, resolution: Resolution): GalleryItem => {
  const imgResource = {
    src: `data:image/png;base64,${base64Image}`,
    height: resolution.height,
    width: resolution.width
  }
  return {
    type: 'image',
    widthToHeightRatio: resolution.width / resolution.height,
    preview: {
      imageSource: imgResource,
      videoSource: null
    },
    source: [imgResource]
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(AiIcon)),
        _hoisted_4
      ]),
      _createElementVNode("div", {
        class: "image-generator-panel__close-button",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emits('closePanel')))
      }, [
        _createVNode(_unref(UnionIcon))
      ])
    ]),
    _hoisted_5,
    _withDirectives(_createElementVNode("div", _hoisted_6, [
      _createVNode(_unref(LockIcon), { class: "image-generator-panel__unsupported-dialogue-lock-icon" }),
      _createElementVNode("p", _hoisted_7, [
        _createTextVNode("A Serato Studio subscription or license is required to generate images."),
        _createElementVNode("a", {
          href: studioPricingLink,
          class: "image-generator-panel__unsupported-dialogue-link",
          target: "”_blank”"
        }, [
          _createVNode(_unref(ExternalLinkIcon), { class: "image-generator-panel__unsupported-dialogue-link-icon" }),
          _createTextVNode(" "),
          _hoisted_8
        ])
      ])
    ], 512), [
      [_vShow, !_unref(endUserProfileStore).canGenerateImages]
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(['image-generator-panel__body',{'image-generator-panel__body--disabled': !_unref(endUserProfileStore).canGenerateImages}])
    }, [
      _hoisted_9,
      _createElementVNode("div", _hoisted_10, [
        _withDirectives(_createElementVNode("textarea", {
          ref_key: "textAreaRef",
          ref: textAreaRef,
          class: "image-generator-panel__text-area",
          placeholder: "A person making music on a laptop riding a zebra in space as digital art",
          minlength: "3",
          maxlength: "300",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((text).value = $event)),
          onKeydown: _withKeys(_withModifiers(onGenerate, ["exact","prevent"]), ["enter"])
        }, null, 40, _hoisted_11), [
          [_vModelText, text.value]
        ]),
        _createVNode(BigButton, {
          class: "image-generator-panel__button",
          text: "Generate image",
          "is-loading": isFetching.value,
          "is-disabled": text.value.length < 3 || !_unref(endUserProfileStore).canGenerateImages,
          onClick: onGenerate
        }, null, 8, ["is-loading", "is-disabled"]),
        _createVNode(_unref(DallE), { class: "image-generator-panel__attribution" })
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createVNode(_TransitionGroup, { name: "image-generator-panel__slide-transition" }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(backgroundStore).generatedImages, (image, index) => {
              return (_openBlock(), _createElementBlock("div", {
                class: "image-generator-panel__item",
                key: _unref(backgroundStore).generatedImages.length - index
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(['image-generator-panel__image', { 'image-generator-panel__image--selected': _unref(isSelectedBackground)(image) }]),
                  onClick: ($event: any) => (_unref(selectBackground)(image))
                }, [
                  _createElementVNode("img", {
                    class: "image-generator-panel__image-contents",
                    src: image.preview.imageSource.src,
                    onDragstart: ($event: any) => (_unref(onDragBackground)($event, image))
                  }, null, 40, _hoisted_14)
                ], 10, _hoisted_13),
                _createElementVNode("a", {
                  class: "image-generator-panel__download",
                  href: image.source[0].src,
                  download: ""
                }, [
                  _createVNode(_unref(DownloadIcon), { class: "image-generator-panel__download-icon" }),
                  _createTextVNode("Download image ")
                ], 8, _hoisted_15),
                _hoisted_16
              ]))
            }), 128))
          ]),
          _: 1
        }),
        _withDirectives(_createElementVNode("div", _hoisted_17, null, 512), [
          [_vShow, _unref(backgroundStore).generatedImages.length === 0]
        ]),
        _hoisted_18
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createVNode(_unref(FlaskIcon)),
        _createTextVNode(" Text-to-image is an experimental feature - there could be outages during peak times ")
      ])
    ], 2)
  ]))
}
}

})